import React from 'react';
import classNames from 'classnames';
import { IntlProvider } from 'react-intl-next';
import { format } from 'date-fns';
import type { HwmUsageTableProps } from '@atlassian/old-usage-graph';
import { UsageGraph } from '@atlassian/usage-graph';
import { UsageGraph as OldUsageGraph } from '@atlassian/old-usage-graph';
import { DATE_FORMAT } from '@css-xp/ppc-constants';
import Lozenge from '@atlaskit/lozenge';
import { Flex, Text, xcss } from '@atlaskit/primitives';
import { UserHistoryGraphTooltip } from './user-history-graph-tooltip';
import styled from '@emotion/styled';

type UserHistoryGraphProps = {
  isDesignIteration1Enabled?: boolean;
  isNewHWBGraphChangesEnabled?: boolean;
};

const BlueCircle = styled.div`
  height: 16px;
  width: 16px;
  background-color: #1d7afc;
  border-radius: 50%;
`;

const WhiteCircle = styled.div`
  height: 16px;
  width: 16px;
  background-color: #ffffff;
  border-radius: 50%;
  border: 1px solid #ccc;
`;

const HeadingStyles = xcss({
  marginLeft: 'auto',
  paddingRight: 'space.400',
});

type HighwatermarkGraphProps = Omit<
  HwmUsageTableProps,
  'tooltip' | 'formatXTick' | 'formatYTick' | 'onMouseEnterChart'
>;
const MONTH_DAY_FORMAT = 'MMM d';
export const UserHistoryGraph: React.ComponentType<UserHistoryGraphProps & HighwatermarkGraphProps> = ({
  period,
  points,
  usageLabel,
  interpolationPeriod,
  isDesignIteration1Enabled,
  isNewHWBGraphChangesEnabled,
}) => {
  const usageStartDate = format(new Date(period.start), DATE_FORMAT);
  const usageEndDate = format(new Date(period.end), DATE_FORMAT);

  const renderHeader = () => {
    if (isDesignIteration1Enabled) {
      return isNewHWBGraphChangesEnabled ? (
        <Flex>
          <Flex direction="column" gap="space.075">
            <h2>Billed users</h2>
            <Text color="color.text.subtle">
              Keep in mind that data usage can take a couple of hours to update here.
            </Text>
          </Flex>
        </Flex>
      ) : (
        <Flex direction="column" gap="space.075">
          <h2>Usage tracker</h2>
          <Text>{`${usageStartDate} to ${usageEndDate}`}</Text>
        </Flex>
      );
    }

    return (
      <>
        <div className="usage-graph-history-label">Current billing: </div>
        <div className="usage-graph-history-value">{`${usageStartDate} - ${usageEndDate}`}</div>
      </>
    );
  };

  return (
    <div
      className={classNames('usage-graph-history', {
        'usage-graph-history-v2': isDesignIteration1Enabled,
      })}
    >
      {!isNewHWBGraphChangesEnabled && (
        <div className="usage-graph-version">
          <Lozenge appearance="new">Beta</Lozenge>
        </div>
      )}
      <div className="usage-graph-history-header">
        {renderHeader()}
        {isNewHWBGraphChangesEnabled ? (
          <Flex direction="column" gap="space.100" alignItems="center" xcss={HeadingStyles}>
            <Text>{`${usageStartDate} to ${usageEndDate}`}</Text>
            <Flex gap="space.200">
              <Flex alignItems="center" gap="space.050">
                <WhiteCircle />
                <Text>Users</Text>
              </Flex>
              <Flex alignItems="center" gap="space.050">
                <BlueCircle />
                <Text>Billed user quantity</Text>
              </Flex>
            </Flex>
          </Flex>
        ) : (
          <div className="usage-graph-history-tick-format">
            <div className="usage-graph-circle" />
            <p> Users </p>
            <div className="usage-graph-square" />
            <p> Maximum billable usage</p>
          </div>
        )}
      </div>
      <div className="usage-graph-wrapper">
        {isNewHWBGraphChangesEnabled ? (
          <IntlProvider locale="en">
            <UsageGraph billingPeriod={period} points={points} graphingPeriod={interpolationPeriod} />
          </IntlProvider>
        ) : (
          <OldUsageGraph
            period={period}
            points={points}
            tooltip={UserHistoryGraphTooltip}
            usageLabel={usageLabel}
            formatXTick={(tick: any) => format(new Date(tick), MONTH_DAY_FORMAT)}
            isAnimationActive={false}
            interpolationPeriod={interpolationPeriod}
          />
        )}
      </div>
    </div>
  );
};
